/**
 * UnsavedChangesWarning Component
 *
 * Displays a warning banner when a survey has unsaved changes.
 * Shared across all survey types (Custom, Event Registration, Satisfaction, Donation)
 *
 * ## Features
 * - **Accessibility**: Includes ARIA attributes (role="alert", aria-live="polite")
 * - **Visual Design**: Yellow warning background matching application patterns
 * - **Animation**: Smooth 300ms slide-down entrance effect
 * - **Conditional Rendering**: Only displays when hasUnsavedChanges is true
 * - **Customizable**: Supports custom messages and className overrides
 *
 * ## Positioning
 * Placed in the page layout between:
 * - SurveyActions header (Save/Cancel buttons)
 * - TabRoot component (Settings/Questions/Preview tabs)
 *
 * This ensures the warning is:
 * - Visible immediately after the header
 * - Above the content being edited
 * - Consistent across all survey types
 *
 * ## User Experience
 * - Appears immediately when changes are detected
 * - Disappears when changes are saved
 * - Works in conjunction with browser navigation prevention
 * - Does not interrupt user workflow (polite announcement)
 *
 * ## Default Message
 * "To see the most recent changes in the live survey, please save this survey."
 *
 * This message clarifies that:
 * - Changes won't appear in the live survey until saved
 * - Respondents are seeing the old version
 * - Action required: click Save button
 */

import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { LightBulbIcon } from '../../../../../components/svgs/icons/LightBulbIcon';
import { messageWarning } from '../../../../../styles/colors';

/**
 * Props for UnsavedChangesWarning component
 */
export interface UnsavedChangesWarningProps {
	/**
	 * Whether to show the warning banner
	 *
	 * Typically comes from the useUnsavedChanges hook, which returns true when:
	 * - Survey is in edit mode (not creation)
	 * - AND any changes have been detected:
	 *   - React Hook Form isDirty/dirtyFields
	 *   - Custom form field changes
	 *   - Survey-specific changes (metadata, images, settings)
	 *
	 * When false: Component returns null (not rendered)
	 * When true: Component displays warning banner
	 */
	hasUnsavedChanges: boolean;

	/**
	 * Optional custom message to display in the warning banner
	 *
	 * @default "To see the most recent changes in the live survey, please save this survey."
	 *
	 * Use cases for custom messages:
	 * - Survey-specific instructions
	 * - Localization/internationalization
	 * - Additional context for specific warning scenarios
	 *
	 * The default message is carefully worded to:
	 * - Explain impact: "changes won't appear in live survey"
	 * - Indicate action: "please save"
	 * - Be clear but not alarming
	 */
	message?: string;

	/**
	 * Optional additional className for styling overrides
	 *
	 * Applied in addition to the default styles, allowing:
	 * - Survey-specific positioning adjustments
	 * - Custom spacing or layout modifications
	 * - Additional visual treatments
	 *
	 * Note: The default yellow warning background and core styles
	 * remain intact to maintain consistency across survey types.
	 */
	className?: string;

	/**
	 * Test ID for automated testing
	 *
	 * @default "unsaved-changes-warning"
	 *
	 * Used in tests to:
	 * - Verify warning appears when changes exist
	 * - Verify warning disappears when changes saved
	 * - Test accessibility attributes
	 * - Test message customization
	 */
	testId?: string;
}

/**
 * Warning component to notify users of unsaved survey changes
 *
 * @example Basic usage
 * ```tsx
 * <UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges} />
 * ```
 *
 * @example With custom message
 * ```tsx
 * <UnsavedChangesWarning
 *   hasUnsavedChanges={hasUnsavedChanges}
 *   message="You have unsaved changes. Click Save to preserve your work."
 * />
 * ```
 *
 * @example With custom styling
 * ```tsx
 * <UnsavedChangesWarning
 *   hasUnsavedChanges={hasUnsavedChanges}
 *   className={css(customStyles.extraMargin)}
 * />
 * ```
 */
export const UnsavedChangesWarning: React.FC<UnsavedChangesWarningProps> = ({
	hasUnsavedChanges,
	message = 'To see the most recent changes in the live survey, please save this survey.',
	className,
	testId = 'unsaved-changes-warning',
}) => {
	// Early return: Don't render anything if no unsaved changes
	// This keeps the DOM clean and avoids unnecessary screen reader announcements
	if (!hasUnsavedChanges) {
		return null;
	}

	return (
		<div
			// ARIA: Identifies this as an alert for screen readers
			// Screen readers will announce this content when it appears
			role='alert'
			// ARIA: Polite announcement - won't interrupt current screen reader activity
			// Alternative would be "assertive" which interrupts immediately
			// "polite" is appropriate here as this isn't a critical error
			aria-live='polite'
			// Apply both default styles and optional custom className
			className={className ? `${css(styles.warningMessage)} ${className}` : css(styles.warningMessage)}
			// Test ID for automated testing - allows tests to find this element
			data-testid={testId}
		>
			<span className={css(styles.messageContent)}>
				{message}
				<LightBulbIcon width={16} height={16} fill='#856404' styles={[styles.icon]} />
			</span>
		</div>
	);
};

/**
 * Styles for UnsavedChangesWarning
 *
 * Design matches existing warning patterns throughout the application
 * for consistent user experience.
 */
const styles = StyleSheet.create({
	warningMessage: {
		// Visual Design
		background: messageWarning, // Yellow warning color from design system
		fontSize: 14, // Standard body text size
		textAlign: 'center', // Center-align for banner-style warnings
		padding: 11, // Comfortable padding for readability

		// Layout
		position: 'relative', // Allows z-index to work
		zIndex: 1, // Ensures warning appears above other content
		marginBottom: 20, // Space between warning and tabs below

		// Animation: Smooth slide-down entrance effect
		// When component first renders, it slides down from -10px with fade-in
		// This draws attention without being jarring
		animationName: {
			from: {
				opacity: 0, // Start invisible
				transform: 'translateY(-10px)', // Start 10px above final position
			},
			to: {
				opacity: 1, // End fully visible
				transform: 'translateY(0)', // End at final position
			},
		},
		animationDuration: '300ms', // 0.3 second animation (quick but smooth)
		animationTimingFunction: 'ease-out', // Deceleration curve (natural motion)
		animationFillMode: 'both', // Apply both from/to styles (prevents flicker)

		// Animation respects user's reduced motion preferences automatically
		// via CSS prefers-reduced-motion media query (handled by browser)
	},
	messageContent: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8, // Space between message text and icon
	},
	icon: {
		flexShrink: 0, // Prevent icon from shrinking
		marginLeft: 4, // Additional spacing from text
	},
});
